import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Menu } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { slugify } from '@/features/products';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useCreateMenu, useUpdateMenu } from '../hooks/use-menu-mutations';
import {
  emptyMenuForm,
  fromMenu,
  menuFormSchema,
  toMenuCreate,
  toMenuUpdate,
  type MenuForm,
} from '../schemas/menu-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['title', 'handle']);

export interface MenuDialogProps {
  storeId: string;
  /** Null creates a new, empty menu. */
  menu: Menu | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (menu: Menu) => void;
}

function MenuFormBody({
  storeId,
  menu,
  onClose,
  onCreated,
}: {
  storeId: string;
  menu: Menu | null;
  onClose: () => void;
  onCreated?: ((menu: Menu) => void) | undefined;
}) {
  const { t } = useTranslation(['menus', 'common']);
  const message = useErrorMessage();
  const create = useCreateMenu(storeId);
  const update = useUpdateMenu(storeId);
  const pending = create.isPending || update.isPending;
  const [handleTouched, setHandleTouched] = useState(menu !== null);
  const form = useForm<MenuForm>({
    resolver: useI18nResolver(menuFormSchema),
    defaultValues: menu ? fromMenu(menu) : emptyMenuForm(),
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      if (menu) {
        await update.mutateAsync({ id: menu.id, body: toMenuUpdate(values) });
        onClose();
      } else {
        const created = await create.mutateAsync(toMenuCreate(values));
        onClose();
        onCreated?.(created);
      }
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('menus:fields.title')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!handleTouched) {
                      form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="handle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('menus:fields.handle')}</FormLabel>
              <FormControl>
                <Input
                  className="font-mono text-[13px]"
                  {...field}
                  onChange={(e) => {
                    setHandleTouched(true);
                    field.onChange(e);
                  }}
                />
              </FormControl>
              <FormDescription>{t('menus:fields.handleHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending
              ? t('common:actions.saving')
              : menu
                ? t('menus:edit.submit')
                : t('menus:create.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Creates a menu, or renames one (title and handle); items are edited in the tree. */
export function MenuDialog({ storeId, menu, open, onOpenChange, onCreated }: MenuDialogProps) {
  const { t } = useTranslation('menus');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{menu ? t('edit.title') : t('create.title')}</DialogTitle>
          <DialogDescription>
            {menu ? t('edit.description') : t('create.description')}
          </DialogDescription>
        </DialogHeader>
        <MenuFormBody
          storeId={storeId}
          menu={menu}
          onClose={() => onOpenChange(false)}
          onCreated={onCreated}
        />
      </DialogContent>
    </Dialog>
  );
}
